import { ethers } from 'ethers';
import { supabase } from './supabaseClient';

// Sepolia API key from your memories
const SEPOLIA_RPC_URL = "https://eth-sepolia.g.alchemy.com/v2/_43V82zF2JrnEnxBkJz0PTjBq9w-R9dz";

/**
 * Fetch real-time wallet balance from Sepolia network
 * @param {string} address - Wallet address to check
 * @returns {Promise<string>} - Formatted balance string (e.g. "0.0123 ETH")
 */
export const getWalletBalanceFromChain = async (address) => {
  try {
    const provider = new ethers.providers.JsonRpcProvider(SEPOLIA_RPC_URL);
    const balance = await provider.getBalance(address);
    const formattedBalance = ethers.utils.formatEther(balance);
    return `${parseFloat(formattedBalance).toFixed(4)} ETH`; // formatted nicely
  } catch (error) {
    console.error('Error fetching balance from chain:', error);
    return '0.0000 ETH'; // fallback
  }
};

/**
 * Sync wallet balance from blockchain to Supabase
 * @param {string} address - Wallet address
 * @returns {Promise<object|null>} - Updated wallet object or null
 */
export const syncWalletBalanceToSupabase = async (address) => {
  try {
    // Get balance from blockchain
    const provider = new ethers.providers.JsonRpcProvider(SEPOLIA_RPC_URL);
    const balance = await provider.getBalance(address);
    const formattedBalance = ethers.utils.formatEther(balance);
    const numericBalance = parseFloat(formattedBalance);
    
    // Check if wallet exists in Supabase
    const { data: wallet, error: fetchError } = await supabase
      .from('wallets')
      .select('*')
      .eq('address', address)
      .eq('token', 'ETH')
      .single();
    
    if (fetchError && fetchError.code !== 'PGRST116') { // PGRST116 is "no rows returned"
      console.error('Error fetching wallet:', fetchError);
      return null;
    }
    
    if (wallet) {
      // Update existing wallet
      const { data, error } = await supabase
        .from('wallets')
        .update({ 
          balance: numericBalance,
          last_updated: new Date().toISOString()
        })
        .eq('id', wallet.id)
        .select();
      
      if (error) {
        console.error('Error updating wallet balance:', error);
        return null;
      }
      
      return data?.[0] || null;
    } else {
      // Create new wallet entry
      const { data, error } = await supabase
        .from('wallets')
        .insert([
          {
            address: address,
            token: 'ETH',
            balance: numericBalance,
            last_updated: new Date().toISOString()
          }
        ])
        .select();
      
      if (error) {
        console.error('Error creating wallet:', error);
        return null;
      }
      
      return data?.[0] || null;
    }
  } catch (error) {
    console.error('Error syncing wallet balance:', error);
    return null;
  }
};

/**
 * Get all transactions for a wallet
 * @param {string} address - Wallet address
 * @returns {Promise<Array>} - Array of transactions
 */
export const getAllWalletTransactions = async (address) => {
  try {
    // Get sent transactions
    const { data: sentTx, error: sentError } = await supabase
      .from('transactions')
      .select('*')
      .eq('from_address', address)
      .order('created_at', { ascending: false });
    
    if (sentError) {
      console.error('Error fetching sent transactions:', sentError);
      return [];
    }
    
    // Get received transactions
    const { data: receivedTx, error: receivedError } = await supabase
      .from('transactions')
      .select('*')
      .eq('to_address', address)
      .order('created_at', { ascending: false });
    
    if (receivedError) {
      console.error('Error fetching received transactions:', receivedError);
      return [];
    }
    
    // Combine and sort by timestamp
    const allTransactions = [...(sentTx || []), ...(receivedTx || [])];
    return allTransactions.sort((a, b) => 
      new Date(b.created_at) - new Date(a.created_at)
    );
  } catch (error) {
    console.error('Error getting all wallet transactions:', error);
    return [];
  }
};

/**
 * Format transaction for display
 * @param {object} transaction - Transaction object from Supabase
 * @param {string} walletAddress - Current wallet address
 * @returns {object} - Formatted transaction object
 */
export const formatTransactionForDisplay = (transaction, walletAddress) => {
  // Determine if this is a send or receive transaction from the perspective of the current wallet
  const isSend = transaction.from_address.toLowerCase() === walletAddress.toLowerCase();
  const type = isSend ? 'send' : 'receive';
  
  return {
    id: transaction.id,
    type: transaction.type || type,
    amount: transaction.amount,
    token: transaction.token,
    timestamp: transaction.created_at,
    status: transaction.status,
    hash: transaction.transaction_hash,
    from: transaction.from_address,
    to: transaction.to_address,
    gasFee: transaction.gas_fee,
    network: transaction.network
  };
};
